import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { AppNotificationUtilClass, Plani8DataService } from '@monorepo/services';
import * as TTnaUpdateActions from './tna-updates.actions';
import { TTnaUpdateActionType } from './tna-updates.models';
import { switchMap, map, catchError, finalize, take } from 'rxjs/operators';
import { from, of } from 'rxjs';
import { TNA_UPDATES_ITEMS_PER_PAGE_LIST } from './tna-updates.facade';
import { TRESPONSE_BULK_COMPLETE_TNA_ORDER_STEPS } from '@monorepo/types';

@Injectable({ providedIn: 'root' })
export class TnaUpdateEffects {
  constructor(
    private actions$: Actions,
    private plani8DataService: Plani8DataService,
    private appNotificationUtilClass: AppNotificationUtilClass,
  ) {}

  fetchTnaStepsList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTnaUpdateActionType.GetTnaOrderStepList),
      switchMap((action: TTnaUpdateActions.GetTnaOrderStepList) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loader) => {
            return [loader, action] as const;
          }),
        ),
      ),
      switchMap(([loader, action]) =>
        this.plani8DataService.fetchTnaOrderStepList(action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loader);
          }),
          map((res) => new TTnaUpdateActions.GetTnaOrderStepListSuccess(res, action.fetchReason)),
          catchError(() => {
            return of(new TTnaUpdateActions.GetTnaOrderStepListFailure());
          }),
        ),
      ),
    ),
  );

  fetchTnaSteps$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTnaUpdateActionType.GetTnaOrderStep),
      switchMap((action: TTnaUpdateActions.GetTnaOrderStep) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loader) => {
            return [loader, action] as const;
          }),
        ),
      ),
      switchMap(([loader, action]) =>
        this.plani8DataService.getTnaOrderStep(action.tosId).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loader);
          }),
          map((res) => {
            return new TTnaUpdateActions.GetTnaOrderStepSuccess(res, action.tosId);
          }),
          catchError(() => {
            return of(new TTnaUpdateActions.GetTnaOrderStepFailure());
          }),
        ),
      ),
    ),
  );

  updateTnaSteps$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTnaUpdateActionType.UpdateTnaOrderStep),
      switchMap((action: TTnaUpdateActions.UpdateTnaOrderStep) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loader) => {
            return [loader, action] as const;
          }),
        ),
      ),
      switchMap(([loader, action]) =>
        this.plani8DataService.updateTnaStep(action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loader);
          }),
          map(() => {
            return new TTnaUpdateActions.GetTnaOrderStepList(action.listingPayload, 'STEP_ACTION_UPDATE');
          }),
          catchError(() => {
            return of(new TTnaUpdateActions.UpdateTnaOrderStepFailure());
          }),
        ),
      ),
    ),
  );

  bulkCompleteTnaOrderSteps$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTnaUpdateActionType.BulkCompleteTnaOrderSteps),
      switchMap((action: TTnaUpdateActions.BulkCompleteTnaOrderSteps) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loader) => [loader, action] as const),
        ),
      ),
      switchMap(([loader, action]) =>
        this.plani8DataService.bulkCompleteTnaOrderSteps(action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loader);
          }),
          map((response) => {
            this.presentBulkCompleteToast(response);
            return new TTnaUpdateActions.GetTnaOrderStepList(action.listingPayload, 'STEP_ACTION_UPDATE');
          }),
          catchError(() => of(new TTnaUpdateActions.BulkCompleteTnaOrderStepsFailure())),
        ),
      ),
    ),
  );

  private presentBulkCompleteToast(response: TRESPONSE_BULK_COMPLETE_TNA_ORDER_STEPS) {
    const { completedCount, failedCount } = response;

    this.appNotificationUtilClass.presentToast(
      failedCount === 0
        ? 'Selected steps marked as completed'
        : completedCount === 0
          ? 'Selected steps could not be completed due to pending prerequisites'
          : `Partial completion: ${completedCount} steps completed, ${failedCount} steps not completed due to pending prerequisites`,
    );
  }
}
